import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import {
  AcaoInsight,
  InsightItem,
  SuperficieInsight,
  obterInsights,
  registrarBalao,
  registrarExibicoes,
  registrarInteracao,
} from '../../services/insights';
import { rastrearEventoFunil } from '../../services/rastreamentoFunil';

/** Abre o painel do assistente numa aba: window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_ASSISTENTE, { detail: { aba: 'insights' } })). */
export const EVENTO_ABRIR_ASSISTENTE = 'open-assistente';
/** O avatar do canto: abre na aba Insights ou fecha, se já estiver aberto nela. */
export const EVENTO_ALTERNAR_ASSISTENTE = 'alternar-assistente';

export type AbaAssistente = 'insights' | 'ajuda';

/** Ações que tiram o card da tela na hora (o servidor também passa a escondê-lo). */
const ACOES_QUE_ESCONDEM: AcaoInsight[] = ['cta', 'depois', 'nao_util', 'dispensado'];

/**
 * Cada tela monta o próprio layout (e as telas visitadas ficam em cache), então há um provider por tela.
 * O card escondido numa delas é avisado às outras por este evento.
 */
const EVENTO_INSIGHT_ESCONDIDO = 'insight-escondido';
const EVENTO_BALAO_MOSTRADO = 'insight-balao-mostrado';

const INTERVALO_RECARGA_MS = 30 * 60 * 1000;
const ESPERA_ENVIO_EXIBICOES_MS = 2000;

interface ValorInsights {
  habilitado: boolean;
  carregado: boolean;
  itens: InsightItem[];
  balao: InsightItem | null;
  /** O balão apareceu: registra e garante que não aparece de novo hoje, em nenhuma tela. */
  marcarBalaoMostrado: (item: InsightItem) => void;
  recarregar: () => Promise<void>;
  registrarExibicao: (item: InsightItem, superficie: SuperficieInsight) => void;
  interagir: (item: InsightItem, acao: AcaoInsight, superficie: SuperficieInsight, posicao?: number) => void;
}

const InsightsContext = createContext<ValorInsights>({
  habilitado: false,
  carregado: false,
  itens: [],
  balao: null,
  marcarBalaoMostrado: () => undefined,
  recarregar: async () => undefined,
  registrarExibicao: () => undefined,
  interagir: () => undefined,
});

export const useInsights = () => useContext(InsightsContext);

/**
 * Busca os insights do usuário (ao entrar, ao trocar de filial e a cada 30 min) e concentra o registro do
 * que ele vê e faz. Fica fora do Redux persistido de propósito: é dado do dia, não deve sobreviver ao reload.
 * Sem o recurso liberado para a empresa, o servidor responde habilitado=false e nada do assistente aparece.
 */
export const InsightsProvider: React.FC<{ ativo: boolean }> = ({ ativo, children }) => {
  const filialSelecionada = useAppSelector(state => state.filialSelecionada);
  const location = useLocation();
  const rotaRef = useRef(location.pathname);
  rotaRef.current = location.pathname;

  const [habilitado, setHabilitado] = useState(false);
  const [carregado, setCarregado] = useState(false);
  const [itens, setItens] = useState<InsightItem[]>([]);
  const [balao, setBalao] = useState<InsightItem | null>(null);

  const recarregar = useCallback(async () => {
    if (!ativo) return;
    try {
      const resposta = await obterInsights(20);
      setHabilitado(resposta.habilitado);
      setItens(resposta.habilitado ? resposta.itens : []);
      setBalao(resposta.habilitado ? resposta.balao ?? null : null);
    } catch {
      // Falha ao buscar insights nunca atrapalha o uso do sistema: o assistente só não aparece.
    } finally {
      setCarregado(true);
    }
  }, [ativo]);

  useEffect(() => {
    recarregar();
    const intervalo = window.setInterval(recarregar, INTERVALO_RECARGA_MS);
    return () => window.clearInterval(intervalo);
  }, [recarregar, filialSelecionada]);

  // Exibições acumuladas e enviadas em lote; cada card conta uma vez por superfície nesta sessão
  // (o servidor ainda limita a uma a cada 6 horas).
  const filaExibicoes = useRef(new Map<SuperficieInsight, Set<string>>());
  const jaRegistrados = useRef(new Set<string>());
  const temporizador = useRef<number | null>(null);

  const enviarExibicoes = useCallback(() => {
    temporizador.current = null;
    const fila = filaExibicoes.current;
    filaExibicoes.current = new Map();
    fila.forEach((ids, superficie) => {
      registrarExibicoes(Array.from(ids), superficie, rotaRef.current).catch(() => undefined);
    });
  }, []);

  const registrarExibicao = useCallback((item: InsightItem, superficie: SuperficieInsight) => {
    const chave = `${superficie}:${item.id}`;
    if (jaRegistrados.current.has(chave)) return;
    jaRegistrados.current.add(chave);

    const ids = filaExibicoes.current.get(superficie) ?? new Set<string>();
    item.ids.forEach(id => ids.add(id));
    filaExibicoes.current.set(superficie, ids);
    if (temporizador.current === null) {
      temporizador.current = window.setTimeout(enviarExibicoes, ESPERA_ENVIO_EXIBICOES_MS);
    }
  }, [enviarExibicoes]);

  useEffect(() => () => {
    if (temporizador.current !== null) {
      window.clearTimeout(temporizador.current);
      enviarExibicoes();
    }
  }, [enviarExibicoes]);

  useEffect(() => {
    const esconder = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      setItens(atuais => atuais.filter(i => i.id !== id));
      setBalao(atual => (atual?.id === id ? null : atual));
    };
    const balaoMostrado = () => setBalao(null);
    window.addEventListener(EVENTO_INSIGHT_ESCONDIDO, esconder);
    window.addEventListener(EVENTO_BALAO_MOSTRADO, balaoMostrado);
    return () => {
      window.removeEventListener(EVENTO_INSIGHT_ESCONDIDO, esconder);
      window.removeEventListener(EVENTO_BALAO_MOSTRADO, balaoMostrado);
    };
  }, []);

  const marcarBalaoMostrado = useCallback((item: InsightItem) => {
    window.dispatchEvent(new Event(EVENTO_BALAO_MOSTRADO));
    registrarBalao(item.ids, rotaRef.current).catch(() => undefined);
    rastrearEventoFunil('assistente_balao_exibido', { detector: item.codigoDetector, tipo: item.tipo });
  }, []);

  const interagir = useCallback((item: InsightItem, acao: AcaoInsight, superficie: SuperficieInsight, posicao?: number) => {
    if (ACOES_QUE_ESCONDEM.includes(acao)) {
      window.dispatchEvent(new CustomEvent(EVENTO_INSIGHT_ESCONDIDO, { detail: item.id }));
    }
    registrarInteracao(item.ids, acao, superficie, rotaRef.current, posicao).catch(() => undefined);
    rastrearEventoFunil(`insight_${acao}`, { detector: item.codigoDetector, tipo: item.tipo, superficie, agrupado: item.agrupado });
  }, []);

  const valor = useMemo<ValorInsights>(() => ({
    habilitado, carregado, itens, balao, marcarBalaoMostrado, recarregar, registrarExibicao, interagir,
  }), [habilitado, carregado, itens, balao, marcarBalaoMostrado, recarregar, registrarExibicao, interagir]);

  return <InsightsContext.Provider value={valor}>{children}</InsightsContext.Provider>;
};
